import { useEffect, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import FileUpload from "@/components/FileUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, ImageIcon } from "lucide-react";

export default function CoverDesigns() {
  const { isAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ name: "", mode: "background", file: null });

  const load = () => api.get("/covers").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!draft.file) return toast.error("Upload a cover image");
    try {
      await api.post("/covers", { name: draft.name || "Untitled Cover", file_id: draft.file.id, file_url: draft.file.url, content_type: draft.file.content_type, mode: draft.mode });
      toast.success("Cover saved");
      setOpen(false);
      setDraft({ name: "", mode: "background", file: null });
      load();
    } catch (e) { toast.error("Could not save cover"); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/covers/${id}`);
      toast.success("Cover deleted");
      load();
    } catch (e) { toast.error(isAdmin ? "Could not delete" : "Only administrators can delete covers"); }
  };

  return (
    <div className="pgt-fade-up space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#C9A34A] font-semibold">Reusable designs</div>
          <h1 className="font-display text-4xl text-[#0B3B2E]">Cover Designs</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#0B3B2E] hover:bg-[#14513D] gap-2" data-testid="cover-add"><Plus className="h-4 w-4" /> New Cover</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-display text-2xl text-[#0B3B2E]">Save a Cover Design</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Cover name</Label>
                <Input value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} placeholder="e.g. Festive Green Cover" data-testid="cover-name" />
              </div>
              <div className="space-y-1.5">
                <Label>Type</Label>
                <Select value={draft.mode} onValueChange={(v) => setDraft((d) => ({ ...d, mode: v }))}>
                  <SelectTrigger data-testid="cover-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="complete">Complete cover</SelectItem>
                    <SelectItem value="background">Background only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <FileUpload label="Cover file" accept=".jpg,.jpeg,.png,.pdf" purpose="cover" testid="cover-file" minWidth={1080} aspectHint={0.8} value={draft.file} onChange={(f) => setDraft((d) => ({ ...d, file: f }))} />
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={create} className="bg-[#0B3B2E] hover:bg-[#14513D]" data-testid="cover-save">Save Cover</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[#C9A34A]/40 bg-[#FAF7F0] py-16 text-center">
          <ImageIcon className="h-10 w-10 mx-auto text-[#C9A34A]/60" />
          <div className="mt-3 text-[#14513D]/70">No saved cover designs yet.</div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {rows.map((c) => (
            <Card key={c.id} data-testid={`cover-card-${c.id}`} className="overflow-hidden border-[#C9A34A]/25">
              <div className="aspect-[4/5] bg-[#0B3B2E]/5 grid place-items-center overflow-hidden">
                {c.content_type === "application/pdf" ? (
                  <div className="text-[#14513D]/60 text-xs">PDF cover</div>
                ) : (
                  <img src={fileUrl(c.file_url)} alt={c.name} className="h-full w-full object-cover" onError={(e) => (e.currentTarget.style.display = "none")} />
                )}
              </div>
              <div className="p-3">
                <div className="font-semibold text-[#0B3B2E] text-sm truncate">{c.name}</div>
                <div className="flex items-center justify-between mt-1">
                  <Badge variant="outline" className="text-[10px] bg-[#C9A34A]/15 text-[#0B3B2E] border-[#C9A34A]/40">{c.mode}</Badge>
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-red-600" onClick={() => remove(c.id)} data-testid={`cover-del-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
                <div className="text-[11px] text-[#14513D]/50 mt-1">by {c.uploaded_by || "—"}</div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
